import React, { useCallback, useMemo } from 'react';
import { GestureResponderEvent, Pressable, Text, View } from 'react-native';
import type { DateTime } from 'luxon';
import { calendarColors, calendarStyles } from './calendar.styles';
import {
  WEEKDAY_LABELS,
  isFutureDay,
  isSameDay,
  isSameMonth,
  monthMatrix,
} from './calendar.dates';
import { DayNumber } from './DayNumber';
import { EventPill } from './EventPill';
import { monthTargetKey, useCalendarDrag } from './calendar.drag';
import type { CalendarEvent } from './calendar.types';

/**
 * On RN-Web a `View`/`Pressable` ref IS the DOM node, so it exposes
 * `getBoundingClientRect`. We read it live at hit-test time (never cache) so the
 * rect reflects the current transform/scroll (the gripp-uzs.15 fix).
 */
type DomViewNode = { getBoundingClientRect?: () => DOMRect };

/** Max pills rendered per cell before the "+N more" affordance (§1). */
const MAX_PILLS_PER_CELL = 3;

export interface MonthGridProps {
  /** Anchor day inside the month to render (any day of that month). */
  anchor: DateTime;
  /** "Today" for the today-cell highlight + DayNumber circle. */
  today: DateTime;
  /** All events for the visible window; bucketed per day internally. */
  events: CalendarEvent[];
  /** Currently-open activity id (highlighted pill). */
  selectedId?: string | null;
  /** Show the asset second line inside each pill (§1 `showAssetInPill`). */
  showAsset?: boolean;
  /** Open an activity (consumer wires `/activity/:id`). */
  onOpenEvent?: (id: string) => void;
  /**
   * Drag-over hook for Phase 3 — the grid-cell index (0–41) currently under a
   * drag, rendered with the accent + inset ring. No drag *logic* lives here yet;
   * P3 wires the DnD that drives this prop + a drop callback.
   */
  dragOverIndex?: number | null;
  /**
   * Cell press passthrough for Phase 3 drop wiring. Receives the cell's day and
   * its grid index. Foundation/mock usage can ignore it.
   */
  onCellPress?: (day: DateTime, index: number) => void;
}

/** ISO-date key for bucketing events by calendar day. */
const dayKey = (d: DateTime): string => d.toFormat('yyyy-LL-dd');

/**
 * Month layout (plan 0017 §1 "Month grid"). 7 cols × 6 rows built with flexbox
 * (RN-Web has no CSS grid). Outer 1px-subtle border, radius 8, overflow hidden;
 * uppercase DOW header on surface-table-header; per-cell today/out-of-month
 * backgrounds; max 3 pills then "+N more". Driven entirely by `events` props —
 * no fetching (the data layer lands in P2).
 */
export const MonthGrid: React.FC<MonthGridProps> = ({
  anchor,
  today,
  events,
  selectedId,
  showAsset = false,
  onOpenEvent,
  dragOverIndex,
  onCellPress,
}) => {
  const cells = useMemo(() => monthMatrix(anchor), [anchor]);
  const drag = useCalendarDrag();

  // Register/unregister a cell's drop target via its DOM node. The target carries
  // a getRect() that reads getBoundingClientRect LIVE at hit-test time — no cached
  // rect, so SlideLayout's slide-in transform and scroll never make it stale.
  const cellRef = useCallback(
    (node: DomViewNode | null, index: number, day: DateTime) => {
      if (!drag?.enabled) return;
      const key = monthTargetKey(index);
      if (!node) {
        drag.unregisterTarget(key);
        return;
      }
      drag.registerTarget(key, {
        index,
        day,
        getRect: () => {
          const r = node.getBoundingClientRect?.();
          return r ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom } : null;
        },
      });
    },
    [drag],
  );

  // Bucket events by day once; cells read their slice by ISO key. gripp-OS does
  // the same Map-by-day in Calendar.tsx's `eventsByDay`.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const key = dayKey(ev.at);
      const arr = map.get(key);
      if (arr) arr.push(ev);
      else map.set(key, [ev]);
    }
    return map;
  }, [events]);

  // Split the 42 cells into 6 rows of 7 for the flexbox layout.
  const rows = useMemo(() => {
    const out: DateTime[][] = [];
    for (let r = 0; r < 6; r++) {
      out.push(cells.slice(r * 7, r * 7 + 7));
    }
    return out;
  }, [cells]);

  return (
    // `gridOuterFill` (flex: 1) lets the month grid fill the column's height so
    // the 6 flex:1 week rows distribute it — without it the grid sizes to content
    // (~one pill per cell) and leaves the lower column empty. Taller cells fit 2+
    // activities (plan 0017 §1 "Max 3 pills"). WeekGrid keeps content sizing.
    <View style={[calendarStyles.gridOuter, calendarStyles.gridOuterFill]}>
      {/* DOW header row */}
      <View style={[calendarStyles.gridRow, calendarStyles.headerRow]}>
        {WEEKDAY_LABELS.map((label) => (
          <View key={label} style={[calendarStyles.gridCol, calendarStyles.headerCell]}>
            <Text style={calendarStyles.headerCellLabel}>{label}</Text>
          </View>
        ))}
      </View>

      {/* 6 week rows */}
      {rows.map((week, rowIndex) => (
        <View key={rowIndex} style={[calendarStyles.gridRow, { flex: 1 }]}>
          {week.map((day, colIndex) => {
            const index = rowIndex * 7 + colIndex;
            const inMonth = isSameMonth(day, anchor);
            const today_ = isSameDay(day, today);
            // Activities on a future day are scheduled/not-yet-fired (gripp-OS
            // `isFutureDay`) — their pills render in the light-red state.
            const future = isFutureDay(day, today);
            // Highlight from the live drag controller OR the explicit prop (the
            // prop lets Storybook/tests force a drag-over state without a pointer).
            const isDragOver =
              dragOverIndex === index || drag?.activeDropKey === monthTargetKey(index);
            const isLastCol = colIndex === 6;
            const dayEvents = eventsByDay.get(dayKey(day)) ?? [];
            // Reserve the last slot for "+N more" when overflowing: show all when
            // events <= MAX, else MAX-1 pills + the more-row so total rows never
            // exceed MAX. Otherwise a 4th "+N more" row pushes past the cell and
            // overflow:hidden clips it (worst with 2-line asset pills).
            const hasOverflow = dayEvents.length > MAX_PILLS_PER_CELL;
            const visible = hasOverflow
              ? dayEvents.slice(0, MAX_PILLS_PER_CELL - 1)
              : dayEvents;
            const overflow = dayEvents.length - visible.length;

            const handlePress = (e: GestureResponderEvent) => {
              e.stopPropagation();
              onCellPress?.(day, index);
            };

            return (
              <Pressable
                key={index}
                ref={(node) => cellRef(node as DomViewNode | null, index, day)}
                onPress={onCellPress ? handlePress : undefined}
                style={[
                  calendarStyles.monthCell,
                  calendarStyles.cellBorder,
                  isLastCol && calendarStyles.cellNoRightBorder,
                  // Background precedence: drag-over > today > out-of-month.
                  isDragOver
                    ? [
                        calendarStyles.cellDragOverRing,
                        { borderColor: calendarColors.dragOverRing },
                      ]
                    : today_
                    ? calendarStyles.cellTodayTint
                    : !inMonth
                    ? calendarStyles.cellOutOfMonth
                    : undefined,
                ]}
              >
                <DayNumber day={day.day} isToday={today_} inMonth={inMonth} />
                {visible.map((ev) => (
                  <EventPill
                    key={`${ev.id}-${ev.kind ?? 'activity'}`}
                    event={ev}
                    selected={selectedId === ev.id}
                    showAsset={showAsset}
                    future={future}
                    onPress={onOpenEvent}
                  />
                ))}
                {overflow > 0 ? (
                  <Text style={calendarStyles.moreLabel}>{`+${overflow} more`}</Text>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      ))}
    </View>
  );
};

MonthGrid.displayName = 'MonthGrid';
